import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import useFlowStore from '../../../components/chatflow/store'
import { TRIGGER_ID } from '../constants'
import { apiNodesToFlow, buildEdges } from '../utils'
import { API_PATHS, ROUTES } from '@/constants'
import apiClient from '@/services/apiClient'
import i18n from '@/i18n'

/**
 * useFlowLoader — Responsible for:
 * 1. Reading the flowId from the route (/flows/:flowId)
 * 2. Loading nodes/edges when flowId is available
 *
 * Receives setNodes/setEdges from React Flow (must live in the parent component)
 * and initialLoadRef to coordinate dirty tracking.
 */
interface UseFlowLoaderParams {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setNodes: (nodes: any[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setEdges: (edges: any[]) => void;
  initialLoadRef: React.MutableRefObject<boolean>;
}

export function useFlowLoader({ setNodes, setEdges, initialLoadRef }: UseFlowLoaderParams) {
  const { flowId: routeFlowId } = useParams<{ flowId: string }>()
  const navigate    = useNavigate()
  const setFlowId  = useFlowStore((s) => s.setFlowId)
  const flowId     = useFlowStore((s) => s.flowId)
  const setFlow    = useFlowStore((s) => s.setFlow)
  const setLoading = useFlowStore((s) => s.setLoading)
  const setDirty   = useFlowStore((s) => s.setDirty)
  const showToast  = useFlowStore((s) => s.showToast)

  /* ── 1. Read flowId from the route ── */
  useEffect(() => {
    if (routeFlowId) setFlowId(routeFlowId)
  }, [routeFlowId, setFlowId])

  /* ── 2. Load flow and build canvas when ID is available ── */
  useEffect(() => {
    if (!flowId) return
    let cancelled = false

    async function load() {
      setLoading(true)
      try {
        const { data: raw } = await apiClient.get<Record<string, unknown>>(
          API_PATHS.FLOWS.BY_ID(flowId!),
        )

        const flowData = (raw.flow as Record<string, unknown>) || raw
        const apiNodes = (raw.nodes as unknown[]) || (flowData.nodes as unknown[]) || []
        const { nodes: _unused, ...flowMeta } = flowData
        void _unused

        // Fix invalid startNodeId
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const nodeIds = (apiNodes as any[]).map((n) => n.nodeId)
        if (flowMeta.startNodeId && !nodeIds.includes(flowMeta.startNodeId) && apiNodes.length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          flowMeta.startNodeId = (apiNodes[0] as any).nodeId
        }

        if (cancelled) return

        setFlow(flowMeta)

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const rfNodes = apiNodesToFlow(apiNodes as any[], flowMeta.startNodeId as string)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const rfEdges = buildEdges(apiNodes as any[], flowMeta.startNodeId as string)

        const triggerNode = {
          id:       TRIGGER_ID,
          type:     'trigger',
          position: { x: 300, y: 20 },
          data:     {},
          deletable: false,
          draggable: true,
        }

        setNodes([triggerNode, ...rfNodes])
        setEdges(rfEdges)

        // Mark initial load as complete for dirty tracking
        initialLoadRef.current = false
        setDirty(false)
      } catch (err) {
        if (!cancelled) {
          showToast(i18n.t('flow.load_error', { message: (err as Error).message }), 'error')
          navigate(ROUTES.FLOWS)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [flowId, setFlow, setNodes, setEdges, setLoading, setDirty, showToast, initialLoadRef, navigate])
}
